import { useRef, useState, useEffect } from 'react'
import styles from './DateInput.module.scss'

interface DateInputProps {
    value: string // YYYY-MM-DD
    onChange: (value: string) => void
    id?: string
    minAge?: number
    minDate?: string // YYYY-MM-DD — date must be on or after this
    onComplete?: () => void
    onError?: (error: string | null) => void
}

const parse = (iso: string): { dd: string; mm: string; yyyy: string } => {
    if (!iso) return { dd: '', mm: '', yyyy: '' }
    const parts = iso.split('-')
    if (parts.length === 3) return { dd: parts[2], mm: parts[1], yyyy: parts[0] }
    return { dd: '', mm: '', yyyy: '' }
}

const validateYear = (yyyy: string): string | null => {
    if (!yyyy) return null
    if (yyyy.length < 4) return 'Enter a 4-digit year'
    const y = parseInt(yyyy, 10)
    if (y < 1900 || y > 2099) return 'Year must be between 1900 and 2099'
    return null
}

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
]

const daysInMonth = (m: number, y?: number): number => {
    if (m === 2) {
        if (y === undefined) return 29 // year unknown — allow 29 until we know better
        return (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) ? 29 : 28
    }
    return [31, 0, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1]
}

const validate = (dd: string, mm: string, yyyy: string, minAge?: number, minDate?: string): string | null => {
    if (!dd && !mm && !yyyy) return null
    const d = parseInt(dd, 10)
    const m = parseInt(mm, 10)
    const y = parseInt(yyyy, 10)
    if (dd.length === 2 && (d < 1 || d > 31)) return 'Day must be 01–31'
    if (mm.length === 2 && (m < 1 || m > 12)) return 'Month must be 01–12'
    // Month-aware day validation — runs as soon as both day and month are complete
    if (dd.length === 2 && mm.length === 2 && m >= 1 && m <= 12 && d >= 1) {
        const knownYear = yyyy.length === 4 ? y : undefined
        const maxDays = daysInMonth(m, knownYear)
        if (d > maxDays) {
            if (m === 2) {
                if (d > 29) return 'February never has more than 29 days'
                // d === 29, year is now known and it's not a leap year
                return `${y} is not a leap year — February has 28 days`
            }
            return `${MONTH_NAMES[m - 1]} only has ${maxDays} days`
        }
    }
    // Only check year range once 4 digits are entered (mid-typing is fine)
    if (yyyy.length === 4) {
        const yearErr = validateYear(yyyy)
        if (yearErr) return yearErr
    }
    if (dd.length === 2 && mm.length === 2 && yyyy.length === 4) {
        // Use UTC construction to avoid DST shifting the date
        const entered = new Date(Date.UTC(y, m - 1, d))
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        if (entered >= today) return 'Date must be in the past'
        if (minAge !== undefined) {
            const cutoff = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate())
            if (entered > cutoff) return `Must be at least ${minAge} years old`
        }
        if (minDate) {
            const parts = minDate.split('-')
            if (parts.length === 3) {
                const floor = new Date(Date.UTC(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10)))
                if (entered < floor) return 'Must be on or after enlistment date'
            }
        }
    }
    return null
}

const combine = (dd: string, mm: string, yyyy: string, minAge?: number, minDate?: string): string => {
    if (dd.length === 2 && mm.length === 2 && yyyy.length === 4 && !validate(dd, mm, yyyy, minAge, minDate)) {
        return `${yyyy}-${mm}-${dd}`
    }
    return ''
}

export const DateInput = ({ value, onChange, id, minAge, minDate, onComplete, onError }: DateInputProps) => {
    const parsed = parse(value)
    const [dd, setDd] = useState(parsed.dd)
    const [mm, setMm] = useState(parsed.mm)
    const [yyyy, setYyyy] = useState(parsed.yyyy)
    const [error, setError] = useState<string | null>(null)

    const wrapperRef = useRef<HTMLDivElement>(null)
    const ddRef = useRef<HTMLInputElement>(null)
    const mmRef = useRef<HTMLInputElement>(null)
    const yyyyRef = useRef<HTMLInputElement>(null)
    const isInternalChange = useRef(false)

    useEffect(() => {
        // Only reset local parts when the parent explicitly cleared the value
        // (not when we ourselves emitted '' during mid-edit)
        if (!value && !isInternalChange.current) {
            setDd('')
            setMm('')
            setYyyy('')
            setError(null)
        }
        isInternalChange.current = false
    }, [value])

    // Re-run validation when minDate changes (e.g. user edits enlistment after discharge is set)
    useEffect(() => {
        if (!dd && !mm && !yyyy) return
        const err = validate(dd, mm, yyyy, minAge, minDate)
        setError(err)
        isInternalChange.current = true
        onChange(err ? '' : combine(dd, mm, yyyy, minAge, minDate))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minDate])

    const handleDd = (raw: string) => {
        const v = raw.replace(/\D/g, '').slice(0, 2)
        setDd(v)
        const err = validate(v, mm, yyyy, minAge, minDate)
        setError(err)
        onError?.(err)
        isInternalChange.current = true
        onChange(err ? '' : combine(v, mm, yyyy, minAge, minDate))
        if (!err && v.length === 2) mmRef.current?.focus()
    }

    const handleMm = (raw: string) => {
        const v = raw.replace(/\D/g, '').slice(0, 2)
        setMm(v)
        const err = validate(dd, v, yyyy, minAge, minDate)
        setError(err)
        onError?.(err)
        isInternalChange.current = true
        onChange(err ? '' : combine(dd, v, yyyy, minAge, minDate))
        if (!err && v.length === 2) yyyyRef.current?.focus()
    }

    const handleYyyy = (raw: string) => {
        const v = raw.replace(/\D/g, '').slice(0, 4)
        setYyyy(v)
        const err = validate(dd, mm, v, minAge, minDate)
        setError(err)
        onError?.(err)
        const combined = err ? '' : combine(dd, mm, v, minAge, minDate)
        isInternalChange.current = true
        onChange(combined)
        if (combined && !err && onComplete) onComplete()
    }

    // Zero-pad a single-digit day/month on blur (e.g. "9" → "09") so the part
    // reaches length 2 and the full-date validation (range, age, min-date
    // ordering) actually runs instead of silently producing an empty value.
    const handleDdBlur = () => {
        // Read the live DOM value, not the closure: the auto-advance focus()
        // fires this blur synchronously before the setDd re-render lands, so the
        // closure `dd` can be stale (e.g. "0" while the input already shows "08").
        const current = ddRef.current?.value ?? dd
        if (current.length !== 1) return
        const padded = current.padStart(2, '0')
        setDd(padded)
        const err = validate(padded, mm, yyyy, minAge, minDate)
        setError(err)
        onError?.(err)
        isInternalChange.current = true
        onChange(err ? '' : combine(padded, mm, yyyy, minAge, minDate))
    }

    const handleMmBlur = () => {
        const current = mmRef.current?.value ?? mm
        if (current.length !== 1) return
        const padded = current.padStart(2, '0')
        setMm(padded)
        const err = validate(dd, padded, yyyy, minAge, minDate)
        setError(err)
        onError?.(err)
        isInternalChange.current = true
        onChange(err ? '' : combine(dd, padded, yyyy, minAge, minDate))
    }

    // Fires when focus leaves the whole date field (not when moving between its
    // sub-inputs). Flags a date that was touched but left incomplete — e.g. a
    // missing year — so the user gets a clear red error instead of a silently
    // empty value. Reads live DOM values; day/month single digits are padded by
    // their own blur handlers so we treat any non-empty part as "present".
    const handleComponentBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (wrapperRef.current?.contains(e.relatedTarget as Node | null)) return
        const d = ddRef.current?.value ?? dd
        const m = mmRef.current?.value ?? mm
        const y = yyyyRef.current?.value ?? yyyy
        if (!d && !m && !y) return
        const complete = d.length >= 1 && m.length >= 1 && y.length === 4
        if (!complete) {
            const err = y.length > 0 && y.length < 4 ? 'Enter a 4-digit year' : 'Please enter a complete date'
            setError(err)
            onError?.(err)
            isInternalChange.current = true
            onChange('')
        }
    }

    return (
        <div id={id} ref={wrapperRef} onBlur={handleComponentBlur}>
            <div className={`${styles.wrapper} ${error ? styles.wrapperError : ''}`}>
                <input
                    ref={ddRef}
                    className={styles.part}
                    placeholder="DD"
                    value={dd}
                    maxLength={2}
                    inputMode="numeric"
                    onChange={(e) => handleDd(e.target.value)}
                    onBlur={handleDdBlur}
                />
                <span className={styles.sep}>/</span>
                <input
                    ref={mmRef}
                    className={styles.part}
                    placeholder="MM"
                    value={mm}
                    maxLength={2}
                    inputMode="numeric"
                    onChange={(e) => handleMm(e.target.value)}
                    onBlur={handleMmBlur}
                    onKeyDown={(e) => {
                        if (e.key === 'Backspace' && mm === '') {
                            e.preventDefault()
                            const el = ddRef.current
                            if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length) }
                        }
                    }}
                />
                <span className={styles.sep}>/</span>
                <input
                    ref={yyyyRef}
                    className={`${styles.part} ${styles.year}`}
                    placeholder="YYYY"
                    value={yyyy}
                    maxLength={4}
                    inputMode="numeric"
                    onChange={(e) => handleYyyy(e.target.value)}
                    onBlur={() => {
                        const currentValue = yyyyRef.current?.value ?? yyyy
                        if (currentValue.length > 0 && currentValue.length < 4) {
                            const err = 'Enter a 4-digit year'
                            setError(err)
                            onError?.(err)
                            isInternalChange.current = true
                            onChange('')
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Backspace' && yyyy === '') {
                            e.preventDefault()
                            const el = mmRef.current
                            if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length) }
                        }
                    }}
                />
            </div>
            {error && <span className={styles.error}>{error}</span>}
        </div>
    )
}
